<template>
    <Form ref="signalForm" class="form" label-position="right" inline>
        <FormItem label="which triggers when">
            <Select v-model="weatherType" data-testid="connect__weather-type-select">
                <Option
                    v-for="weatherTypeOption in WeatherTypeOptions"
                    :key="weatherTypeOption.value"
                    :value="weatherTypeOption.value"
                >
                    {{ weatherTypeOption.label }}
                </Option>
            </Select>
        </FormItem>

        <template v-if="weatherType === 'OPEN_WEATHER_TEMPERATURE'">
            <FormItem label="is">
                <Select v-model="temperatureOperator" data-testid="connect__temperature-operator-select">
                    <Option
                        v-for="operator in TemperatureOperatorOptions"
                        :key="operator.value"
                        :value="operator.value"
                    >
                        {{ operator.label }}
                    </Option>
                </Select>
            </FormItem>

            <FormItem>
                <Input
                    v-model="temperatureValue"
                    data-testid="connect__temperature-value-input"
                    placeholder="Enter amount °C"
                />
            </FormItem>

            <FormItem label="in the location">
                <Select
                    v-model="location"
                    filterable
                    data-testid="connect__weather-location-select"
                    :default-label="locationDisplayName"
                    :remote-method="debouncedGetSuggestions"
                    :loading="locationSuggestionLoading"
                >
                    <Option v-for="suggestion in locationSuggestions" :key="suggestion.value" :value="suggestion.value">
                        {{ suggestion.label }}
                    </Option>
                </Select>
            </FormItem>
        </template>

        <template v-else-if="weatherType === 'OPEN_WEATHER_RAIN' || weatherType === 'OPEN_WEATHER_SNOW'">
            <FormItem label="is">
                <Select v-model="precipitationValue" data-testid="connect__precipitation-select">
                    <Option v-for="prec in PrecipitationOptions" :key="prec.value" :value="prec.value">
                        {{ prec.label }}
                    </Option>
                </Select>
            </FormItem>

            <FormItem label="in the location">
                <Select
                    v-model="location"
                    filterable
                    data-testid="connect__weather-location-select"
                    :default-label="locationDisplayName"
                    :remote-method="debouncedGetSuggestions"
                    :loading="locationSuggestionLoading"
                >
                    <Option v-for="suggestion in locationSuggestions" :key="suggestion.value" :value="suggestion.value">
                        {{ suggestion.label }}
                    </Option>
                </Select>
            </FormItem>
        </template>
    </Form>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import debounce from "lodash.debounce";

import type { SignalForm } from "@/Connect/components/Accounts/PlatformAccountRuleSignal.vue";

import { getLocationSuggestions } from "@/utils/CreativeIntelligenceApi/connect";
import parseTemperatureValueString from "@/Connect/utils/parseTemperatureValueString";

type TemperatureOperator = "<" | "<=" | "===" | ">=" | ">";
type PrecipitationAmount = "LIGHT" | "MODERATE" | "HEAVY";

const WeatherTypeOptions = [
    {
        value: "OPEN_WEATHER_TEMPERATURE",
        label: "Temperature"
    },
    {
        value: "OPEN_WEATHER_RAIN",
        label: "Rain"
    },
    {
        value: "OPEN_WEATHER_SNOW",
        label: "Snow"
    }
];

const TemperatureOperatorOptions = [
    {
        label: "Less than",
        value: "<"
    },
    {
        label: "Less than or equal to",
        value: "<="
    },
    {
        label: "Equal to",
        value: "==="
    },

    {
        label: "Greater than or equal to",
        value: ">="
    },

    {
        label: "Greater than",
        value: ">"
    }
];

const PrecipitationOptions = [
    {
        label: "Light",
        value: "LIGHT"
    },
    {
        label: "Moderate",
        value: "MODERATE"
    },
    {
        label: "Heavy",
        value: "HEAVY"
    }
];

const props = defineProps<{ modelValue: SignalForm }>();

const emit = defineEmits<(e: "update:modelValue", { signalType, signalValue, location }: SignalForm) => void>();

const weatherType = ref(props.modelValue.signalType);
const temperatureOperator = ref<TemperatureOperator | "">("");
const temperatureValue = ref("");
const precipitationValue = ref<PrecipitationAmount | "">("");
const location = ref("");
const locationDisplayName = ref("");

const locationSuggestionLoading = ref(false);
const locationSuggestions = ref<{ label: string; value: string }[]>([]);

watch(
    props.modelValue,
    () => {
        if (props.modelValue.signalType === "OPEN_WEATHER_TEMPERATURE") {
            const { value, operator } = parseTemperatureValueString(props.modelValue.signalValue);

            temperatureOperator.value = operator;
            temperatureValue.value = value;
        } else {
            precipitationValue.value = props.modelValue.signalValue as PrecipitationAmount;
        }

        if (props.modelValue.location) {
            locationDisplayName.value = props.modelValue.location?.displayName;
            location.value = props.modelValue.location?.locationRef;
        }
    },
    { immediate: true }
);

// watch(weatherType, () => {
//     temperatureOperator.value = "";
//     temperatureValue.value = "";
//     precipitationValue.value = "";
// });

async function getSuggestions(query: string) {
    if (!query) {
        locationSuggestions.value = [];

        return;
    }

    locationSuggestionLoading.value = true;

    const { data } = await getLocationSuggestions({ locationQuery: query });

    locationSuggestions.value = data.map((suggestion: { name: string; country: string }) => ({
        label: `${suggestion.name}, ${suggestion.country}`,
        value: `openweather:2.5/weather:q:${suggestion.name},${suggestion.country}`
    }));

    locationSuggestionLoading.value = false;
}

const debouncedGetSuggestions = debounce(getSuggestions, 300);

watch([weatherType, temperatureOperator, temperatureValue, precipitationValue, location], () => {
    if (weatherType.value === "OPEN_WEATHER_TEMPERATURE") {
        if (!temperatureOperator.value || !temperatureValue.value || !location.value) {
            return;
        }

        emit("update:modelValue", {
            signalType: weatherType.value,
            signalValue: `X${temperatureOperator.value}${temperatureValue.value}`,
            location: {
                displayName: location.value.split(":")[3].split(",")[0],
                locationRef: location.value
            }
        });
    } else {
        if (!precipitationValue.value || !location.value) {
            return;
        }

        emit("update:modelValue", {
            signalType: weatherType.value,
            signalValue: precipitationValue.value,
            location: {
                displayName: location.value.split(":")[3].split(",")[0],
                locationRef: location.value
            }
        });
    }
});
</script>

<style>
.ivu-select-input {
    height: 16px;
}
</style>

<style scoped>
.form {
    display: flex;
    flex-wrap: wrap;
}

.form > .ivu-form-item {
    padding: 10px 0;
    margin-bottom: 0;

    display: flex;
}
</style>
